import React from "react";
import { GlobeAltIcon } from "@heroicons/react/24/outline";

/**
 * Shared language source for all six AI Tutor tools.
 *
 * Three of the tool pages (CodeReview, ProjectBuilder, ResumeReview) each
 * carried their own copy of LANGUAGE_MAP, and the other three (Explain,
 * Interview, SkillTest) had none at all — so half the tutor silently ran in
 * English no matter what the learner picked upstream. This module is the one
 * definition, and it mirrors skillgenpro-backend/function/tsgroutes/src/utils/
 * language.js exactly; if a language is added there, add it here too.
 */
export const LANGUAGE_MAP = {
  en: "English",
  hi: "Hindi",
  ta: "Tamil",
  te: "Telugu",
  kn: "Kannada",
  ml: "Malayalam",
  mr: "Marathi",
  gu: "Gujarati",
  bn: "Bengali",
  pa: "Punjabi",
  ur: "Urdu",
  or: "Odia",
  as: "Assamese",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  ja: "Japanese",
  ko: "Korean",
  zh: "Chinese",
  ar: "Arabic",
  ru: "Russian",
};

/** Indian languages first — that is the bulk of the learner base — then the rest. */
export const LANGUAGE_ORDER = [
  "en", "hi", "te", "ta", "kn", "ml", "mr", "gu", "bn", "pa", "ur", "or", "as",
  "es", "fr", "de", "it", "pt", "ja", "ko", "zh", "ar", "ru",
];

export const LANGUAGE_STORAGE_KEY = "tsg.tutor.language";
export const LANGUAGE_MODE_STORAGE_KEY = "tsg.tutor.languageMode";

/** Mirrors normalizeLanguage() in the backend util: accepts a code or a name. */
export const normalizeLanguage = (input, fallback = "en") => {
  if (!input) return { code: "en", name: LANGUAGE_MAP.en };
  const raw = String(input).toLowerCase().trim();
  if (LANGUAGE_MAP[raw]) return { code: raw, name: LANGUAGE_MAP[raw] };
  const byName = Object.entries(LANGUAGE_MAP).find(
    ([, name]) => name.toLowerCase() === raw
  );
  if (byName) return { code: byName[0], name: byName[1] };
  const safe = LANGUAGE_MAP[fallback] ? fallback : "en";
  return { code: safe, name: LANGUAGE_MAP[safe] };
};

/* localStorage can throw in private-browsing / embedded webviews, and a tutor
   page should never blank out over a preference read. */
const readStored = (key) => {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch (e) {
    return null;
  }
};

const writeStored = (key, value) => {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(key, value);
  } catch (e) {
    /* preference simply won't persist this session */
  }
};

/**
 * Resolve and persist the learner's tutor language.
 *
 * Precedence on first load: a previously saved choice wins, then whatever the
 * route or session handed us, then English. Saving happens only on an explicit
 * user change, so an upstream value never silently overwrites a stored choice.
 */
export const useTutorLanguage = ({ initialLanguage, initialMode } = {}) => {
  const [langCode, setLangCodeState] = React.useState(() => {
    const stored = readStored(LANGUAGE_STORAGE_KEY);
    if (stored && LANGUAGE_MAP[stored]) return stored;
    return normalizeLanguage(initialLanguage).code;
  });

  const [languageMode, setLanguageModeState] = React.useState(() => {
    const stored = readStored(LANGUAGE_MODE_STORAGE_KEY);
    if (stored === "pure" || stored === "mixed") return stored;
    // Default to "mixed" rather than null. ProjectBuilderPage used to send
    // languageMode: null on the first request because its state started null
    // and the computed fallback was never wired to the request body.
    return initialMode === "pure" ? "pure" : "mixed";
  });

  const setLanguage = React.useCallback((next) => {
    const { code } = normalizeLanguage(next);
    setLangCodeState(code);
    writeStored(LANGUAGE_STORAGE_KEY, code);
  }, []);

  const setLanguageMode = React.useCallback((next) => {
    const mode = next === "pure" ? "pure" : "mixed";
    setLanguageModeState(mode);
    writeStored(LANGUAGE_MODE_STORAGE_KEY, mode);
  }, []);

  return {
    langCode,
    langName: LANGUAGE_MAP[langCode] || "English",
    languageMode,
    setLanguage,
    setLanguageMode,
  };
};

/**
 * The header control. Deliberately a native <select>: 23 options in a custom
 * popover is a scroll-trap on phones, and the native picker gets platform
 * search and accessibility for free.
 */
export const LanguagePicker = ({ value, onChange, className = "", compact = false }) => (
  <label
    className={`relative flex items-center gap-1.5 rounded-xl border border-[#E9E7F3] bg-white pl-2.5 pr-1.5 py-1.5 hover:border-[#5B21B6] transition-colors cursor-pointer ${className}`}
    title="Response language"
  >
    <GlobeAltIcon className="w-4 h-4 text-[#5B21B6] shrink-0" />
    {!compact ? (
      <span className="hidden md:inline text-[9px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">
        Language
      </span>
    ) : null}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Response language"
      className="appearance-none bg-transparent text-[12px] font-black text-[#111827] pr-4 focus:outline-none cursor-pointer"
    >
      {LANGUAGE_ORDER.filter((code) => LANGUAGE_MAP[code]).map((code) => (
        <option key={code} value={code}>
          {LANGUAGE_MAP[code]}
        </option>
      ))}
    </select>
    <svg
      className="w-3 h-3 text-[#94A3B8] absolute right-2 pointer-events-none"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  </label>
);

export default useTutorLanguage;
